// Registerer replacement for the softphone's reconnect-in-place path.
//
// A sip.js Registerer reuses its Call-ID for every REGISTER it sends (RFC
// 3261 §10.2). FreeSWITCH keys the NAT OPTIONS ping it sends to a binding by
// that same Call-ID, so a ping that left on the dead socket and times out
// after the transport healed marks the *re-registered* binding unreachable
// and expires it — while the client's Registerer still reads Registered.
// Re-registering on a fresh instance (new Call-ID) keeps the stale ping's
// failure from landing on the new binding.

// The slice of sip.js' Registerer the swap relies on. Structural so the
// sequence can be exercised without a UserAgent.
export interface SwappableRegisterer {
  readonly state: string;
  register(options?: SwapRegisterOptions): Promise<unknown>;
  dispose(): Promise<void>;
  stateChange: {
    addListener(listener: (state: string) => void): void;
  };
}

export interface SwapRegisterOptions {
  requestDelegate?: {
    onAccept?(response: RegisterResponse): void;
    onReject?(response: RegisterResponse): void;
  };
}

interface RegisterResponse {
  message: {
    statusCode: number | undefined;
    reasonPhrase: string | undefined;
  };
}

export interface RegistererSlot<T> {
  registerer: T | null;
}

// `unregistered()` is `private` in sip.js' typings but a plain method on the
// compiled class: it clears the refresh/expiry timers and moves the state to
// Unregistered without sending anything.
interface RegistererInternals {
  unregistered(): void;
}

/**
 * Point `slot` at `fresh`, retire whatever it held, and send the fresh
 * instance's first REGISTER. Resolves once the request is handed to the
 * transport; it rejects only for a REGISTER already in flight or a synchronous
 * throw. Every final response — including the 408/503 sip.js synthesizes for
 * a timeout or transport error — arrives via `options.requestDelegate` and the
 * instance's own stateChange, never as a rejection here.
 *
 * The slot is swapped before the old instance is touched so its closing state
 * changes fail the hook's `registerer !== current` guard.
 */
export function swapRegisterer<T extends SwappableRegisterer>(
  slot: RegistererSlot<T>,
  fresh: T,
  options?: SwapRegisterOptions
): Promise<unknown> {
  const previous = slot.registerer;
  slot.registerer = fresh;
  if (previous && previous !== fresh) {
    retireRegistererSilently(previous);
  }
  return fresh.register(options);
}

/**
 * Drop a Registerer without an un-REGISTER.
 *
 * `Registerer.dispose()` sends REGISTER expires=0 when the state is
 * Registered. The internal profile runs multiple-registrations=false, where
 * FreeSWITCH handles that as "delete every binding for this user" — it would
 * take the replacement's binding down with it and fire sofia::unregister. So
 * the state is forced to Unregistered first; dispose() then only terminates.
 *
 * A REGISTER still in flight on the old instance can be accepted after that
 * and flip it back to Registered before dispose() gets to close — the one
 * window where dispose() would send the un-REGISTER — so the state is pinned.
 */
export function retireRegistererSilently(registerer: SwappableRegisterer): void {
  // Terminated is final: sip.js throws on any transition out of it, and
  // dispose() on a disposed instance is a no-op.
  if (registerer.state === 'Terminated') {
    return;
  }
  const internals = registerer as SwappableRegisterer & RegistererInternals;
  internals.unregistered();
  registerer.stateChange.addListener((state) => {
    if (state === 'Registered') {
      internals.unregistered();
    }
  });
  void registerer.dispose().catch(() => undefined);
}
